import React from 'react';
import PropTypes from 'prop-types';
import { LuFileText, LuLoaderCircle, LuMap, LuMapPin, LuMapPinned, LuPencil, LuPower, LuTrash2, LuUserCheck, LuUserRound, LuUsersRound } from 'react-icons/lu';
import DetailPane from '../../../components/data/DetailPane';
import useCenterText from '../centerText';
import { lookupShape } from './CenterCell';
import { RegionTag, StatusBadge, initialsOf } from './centerBits';

const text = (value) => (value === undefined || value === null || value === '' ? '' : String(value));

/**
 * The selected center's panel (replaces the old action bar): edit, delete
 * (asks first, after checking the projects), status switch and every field.
 * Docked beside the table on wide screens, a drawer otherwise.
 */
const CenterDetailPane = ({ docked, height, center, lookup, checkingDelete, position, onPrev, onNext, onClose, onEdit, onDelete, onToggleStatus }) => {
  const { label } = useCenterText();
  const paneLabel = label('panel.label');

  if (!center) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const active = center['Durum'] === 'Aktif';
  const region = lookup.getRegionForCenter(center);
  const managers = lookup.getRegionManagerName(center);
  const place = [center['İl'], center['İlçe']].filter(Boolean).join(' / ');

  const details = [
    // Rows are plain text; the region's colour is on the tag under the title
    { key: 'region', icon: LuMap, label: label('columns.region'), value: region ? text(region.name) : label('table.noRegion') },
    { key: 'regionManager', icon: LuUserCheck, label: label('columns.regionManager'), value: region ? (managers || label('table.noManager')) : '' },
    { key: 'smo', icon: LuUserRound, label: label('columns.smo'), value: text(lookup.getSMOName(center)) },
    { key: 'smoBackup', icon: LuUsersRound, label: label('columns.smoBackup'), value: text(lookup.getSMOBackupName(center)) },
    { key: 'city', icon: LuMapPinned, label: label('columns.city'), value: text(center['İl']) },
    { key: 'district', icon: LuMapPin, label: label('columns.district'), value: text(center['İlçe']) },
    { key: 'address', icon: LuMapPin, label: label('columns.address'), value: text(center['Adres']), copyable: Boolean(center['Adres']) },
    { key: 'info', icon: LuFileText, label: label('columns.info'), value: text(center['Bilgi']) },
  ];

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={center.id}
      identity={{
        avatar: initialsOf(center['Adı']),
        gradient: active ? 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' : 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
        title: text(center['Adı']) || '-',
        subtitle: place || undefined,
        badges: (
          <>
            <StatusBadge value={center['Durum']} />
            {region && <RegionTag region={region} />}
          </>
        ),
      }}
      actions={[
        { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
        { key: 'delete', label: label('panel.delete'), icon: checkingDelete ? LuLoaderCircle : LuTrash2, spin: checkingDelete, disabled: checkingDelete, tint: 'rose', onClick: onDelete },
      ]}
      details={details}
      footer={{
        label: active ? label('panel.deactivate') : label('panel.activate'),
        title: label('panel.toggleHint'),
        icon: LuPower,
        tone: active ? 'danger' : 'success',
        onClick: onToggleStatus,
      }}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

CenterDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  center: PropTypes.object,
  lookup: lookupShape.isRequired,
  checkingDelete: PropTypes.bool,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
};

export default CenterDetailPane;
